import { z } from 'zod';

import { TrapType } from 'components/AnalyticsPage/TrapType';

interface VulData {
  id: number;
  vul_pk: number;
  create_date: string;
  criticality: string;
  responsible: string;
  inspector: string;
  last_date: string;
  fix_date: string;
  category: string;
}

interface PoisonUsage {
  id: number;
  added_by: string;
  poison_name: string;
  poison_amount: number;
  amount_type: string;
  usage_method: string;
  usage_place: string;
  usage_reason: string;
  added_at: string;
  visit_report: number;
}

interface Encounter {
  id: number;
  discovery_date: string;
  pest_name: string | null;
  amount: number | null;
  pest_traces: string | null;
  witness: string;
  inspection_date: string;
  inspector: string;
  fixed: boolean;
}

interface SignalTimeData {
  amount: number;
  inner_time: string;
}

export interface SignalBaitTimeData {
  added_bait: number;
  eaten_bait: number;
  inner_time: string;
  bait_filling: number;
}

interface SignalData {
  date: string;
  time: SignalTimeData[];
}

export interface SignalBaitData {
  date: string;
  time: SignalBaitTimeData[];
}

interface MonitoringBaitData {
  id: number;
  trap_number: number | null;
  trap_type: string;
  used_poison: string;
  redirect_to_plan_url: string;
  signals_data: string;
}

interface MonitoringData {
  id: number;
  trap_number: number | null;
  trap_type: string;
  trap_serial_number: string;
  install_date: string;
  remove_date: string;
  signals_data: string;
}

interface TrapsData {
  id: number;
  trap_number: number;
  serial_number: string;
  offline_start: string;
  offline_end: string;
  time_range: string;
  lost_msgs: number;
  trap_type?: TrapType;
}

interface Signature {
  username: string;
  signature: string;
}

interface VisitReport {
  id: number;
  visit: number;
  object: string;
  start_date: string;
  end_date: string;
  created_by: string;
  call_date: string;
  is_created: boolean;
  timer_start: string;
  timer_end: string;
  pest_signatured_by: Signature | null;
  customer_signatured_by: Signature | null;
  customer_signature: string;
  started_timer_by: number;
  ended_timer_by: number;
  vuls_data: VulData[];
  enc_data: Encounter[];
  poison_usage: PoisonUsage[];
  monitoring_data: MonitoringData[];
  monitoring_bait_data: MonitoringBaitData[];
  traps_data: TrapsData[];
}

interface VisitReportsData {
  id: number;
  visit: number;
  site_contact: string;
  object: string;
  start_date: string;
  end_date: string;
  created_by: string;
  call_date: string;
  is_created: boolean;
  is_timered: boolean;
}

interface VisitReportsApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: VisitReportsData[];
}

export interface VisitToObject {
  id: number;
  visit_date: string;
  visit_type: string;
}

export const poisonAddSchema = z.object({
  poison_name: z.string().min(1, { message: 'Required field' }),
  poison_amount: z.number().min(1, { message: 'Required field' }),
  amount_type: z.string().min(1, { message: 'Required field' }),
  usage_method: z.string().min(1, { message: 'Required field' }),
  usage_place: z.string().min(1, { message: 'Required field' }),
  usage_reason: z.string().min(1, { message: 'Required field' }),
});

export type PoisonAddFormValues = z.infer<typeof poisonAddSchema>;

// Створення POST # 1 " Створення інтерфейса " | Start Block |

export const startVisitTimerSchema = z.object({
  timer_start: z.string().nullable(),
  started_timer_by: z.number().nullable(),
});

export type StartVisitTimerFormValues = z.infer<typeof startVisitTimerSchema>;

// Створення POST # 1 " Створення інтерфейса " | End Block |

export const endVisitTimerSchema = z.object({
  timer_end: z.string().nullable(),
  ended_timer_by: z.number().nullable(),
});

export type EndVisitTimerFormValues = z.infer<typeof endVisitTimerSchema>;

export const signVisitByPestEmployeeSchema = z.object({
  pest_signatured_by: z.string().nullable(),
});

export type signVisitByPestEmployeeFormValues = z.infer<typeof signVisitByPestEmployeeSchema>;

export const signVisitByCustomerEmployeeSchema = z.object({
  customer_signatured_by: z.string().nullable(),
});

export type signVisitByCustomerEmployeeFormValues = z.infer<typeof signVisitByCustomerEmployeeSchema>;

// Visit report create | Start Block |
export const createVisitReportSchema = z.object({
  start_date: z.string(),
  end_date: z.string(),
  visit_pk: z.number(),
});
export type CreateVisitReportFormValues = z.infer<typeof createVisitReportSchema>;
// Visit report create | End Block |

export {
  VisitReport,
  Encounter,
  VulData,
  PoisonUsage,
  SignalTimeData,
  SignalData,
  MonitoringData,
  TrapsData,
  VisitReportsApiResponse,
  VisitReportsData,
  MonitoringBaitData,
};
